import React, { useEffect, useState, useCallback, useRef, MouseEvent } from 'react';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';

import { SearchBox } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/SearchBox';
import { TextLink } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/TextLink';
import { useWtrDispatch, useWtrSelector } from 'redux/hooks';
import MultiSearch from 'components/MultiSearch';
import SearchHistory from 'components/Search/SearchHistory';
import { useSearchForm } from 'components/Search/SearchForm/use-search-form';
import AutoComplete from 'components/Search/AutoComplete';
import FocusTrap from 'components/FocusTrap';
import { useCookieConsent } from 'contexts/CookieConsent';

import { getAutoSuggestActive } from 'redux/modules/auto-suggest/selectors';
import autoSuggestSearch from 'redux/modules/auto-suggest/actions/auto-suggest-search';
import autoSuggestClear from 'redux/modules/auto-suggest/actions/auto-suggest-clear';
import { updateSearchTerm } from 'redux/modules/search/actions/update-search-term';
import setMobileOverlay from 'redux/modules/page/actions/set-mobile-overlay';

import styles from './MobileOverlay.module.scss';

type Props = {
  clearValue: boolean;
  setClearValue: (value: boolean) => void;
};

const MobileOverlay = ({ clearValue, setClearValue }: Props) => {
  const dispatch = useWtrDispatch();
  const autoSuggestActive = useWtrSelector(getAutoSuggestActive);

  // TODO: Implement redux for search and browse
  // const initialValue = useWtrSelector(getCustomerSearchTerm);
  const initialValue = '';
  // const loading = useWtrSelector(getSearchAndBrowseLoading);

  const { functionalCookiesAccepted } = useCookieConsent();

  const {
    handleChange,
    handleClear,
    handleFocus,
    handleSelectedAutoCompleteItem,
    handleSelectedSearchHistoryItem,
    handleSubmit,
    hasFocus,
    insideRef: formRef,
    inputRef,
    setValue,
    showSearchHistory,
    value,
  } = useSearchForm({
    autoSuggestActive,
    eligibleForAutoSuggest: true,
    functionalCookieConsent: functionalCookiesAccepted,
    initialValue,
    onChange: autoSuggestSearch,
    onClear: autoSuggestClear,
    onSubmit: updateSearchTerm,
    onSearch: setMobileOverlay,
  });

  const inputProps = {
    onFocus: handleFocus,
    ref: inputRef,
    id: 'mobileSearchTerm',
  };

  const insideRef = useRef<HTMLDivElement>(null);

  const [focusTrap, setFocusTrap] = useState(true);

  useEffect(() => {
    const newValue = clearValue ? setValue('') : setValue(initialValue);
    return newValue;
  }, [clearValue, initialValue, setValue]);

  useEffect(
    () => handleFocus(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const lockBodyScroll = useCallback(() => {
    if (insideRef?.current) disableBodyScroll(insideRef.current);
  }, [insideRef]);

  const handleFocusTrap = (isOpen: boolean) => {
    if (isOpen) {
      setFocusTrap(false);
    } else {
      dispatch(setMobileOverlay(false));
    }
  };

  useEffect(() => {
    if (focusTrap) {
      lockBodyScroll();
    } else {
      clearAllBodyScrollLocks();
    }
  }, [focusTrap, lockBodyScroll]);

  useEffect(() => {
    lockBodyScroll();
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [lockBodyScroll]);

  const closeModal = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setClearValue(false);
    dispatch(setMobileOverlay(false));
    dispatch(autoSuggestClear());
  };

  return (
    <FocusTrap active={focusTrap}>
      <div
        ref={insideRef}
        role="dialog"
        aria-label="Search form"
        className={styles.dialog}
        data-test="mobile-overlay"
      >
        <div className={styles.header}>
          <div className={styles.search}>
            {focusTrap && (
              <SearchBox
                ariaActivedescendant={
                  showSearchHistory ? 'search-history-item' : 'autocomplete-suggestion'
                }
                ariaAutocomplete="list"
                ariaOwns={showSearchHistory ? 'search-history' : 'autocomplete-listbox'}
                focus={hasFocus}
                formRef={formRef}
                id="mobileSearch"
                initialValue={initialValue}
                inputProps={inputProps}
                onChange={handleChange}
                onClearClick={handleClear}
                onSubmit={handleSubmit}
                placeholder="Search groceries..."
                value={value}
              />
            )}
          </div>
          <div className={styles.cancel}>
            <TextLink
              component="button"
              role="button"
              onClick={closeModal}
              data-testid="close-overlay"
            >
              Cancel
            </TextLink>
          </div>
        </div>
        <div className={styles.multisearch}>
          <MultiSearch
            hidden={false}
            mobile
            onSearch={() => dispatch(setMobileOverlay(false))}
            onToggleModal={handleFocusTrap}
          />
        </div>
        {!value.length && (
          <SearchHistory inputRef={inputRef} onSelectTerm={handleSelectedSearchHistoryItem} />
        )}
        <AutoComplete
          inline
          inputRef={inputRef}
          onClear={() => dispatch(autoSuggestClear())}
          onSelectItem={handleSelectedAutoCompleteItem}
        />
      </div>
    </FocusTrap>
  );
};

export default MobileOverlay;
